/* eslint-disable @typescript-eslint/camelcase */
import { VuexModule, getModule, Module, Action, Mutation } from "vuex-module-decorators";
import store from "@/store";
import { RevenueDashboardComponent } from "@/app/infrastructures/dependencies/modules/RevenueDashboardComponent";
import { container } from 'tsyringe';
import { RevenueDashboardPresenter } from '../presenters/RevenueDashboardPresenter';
import { RevenueDashboardCustomerListRequest, RevenueOverviewApiRequest, RevenueBreakdownApiRequest } from '@/domain/entities/RevenueDashboard';
import { TopCommodity } from "@/domain/entities/PosRevenue";
import { PosRevenuePresenter } from "../presenters/PosRevenuePresenter";

RevenueDashboardComponent.init();


interface BreakdownFilters {
  serviceTypes: string[]; 
  channels: string[]; 
  productTypes: string[]; 
  destinationCities: string[]; 
  commodities: string[]; 
}


interface ProductTypeOption {
  value: string; 
  label: string; 
}
interface DestinationCityOption {
  value: string; 
  label: string; 
}
interface CommodityOption {
  value: string; // commodity_code
  label: string; // commodity_name
}

interface RevenueDashboardState {
  activeMetricFilter: "omzet" | "stt" | "berat";
  activeBreakdownFilters: BreakdownFilters;

  productTypeOptions: ProductTypeOption[];
  isLoadingProductTypes: boolean;
  destinationCityOptions: DestinationCityOption[];
  isLoadingDestinationCities: boolean;
  destinationCityCurrentPage: number;
  destinationCityHasMore: boolean;
  destinationCityCurrentSearch: string;
  commodityOptions: CommodityOption[];
  isLoadingCommodities: boolean;
  commodityCurrentPage: number;
  commodityHasMore: boolean;
  productTypeCurrentPage: number;
  productTypeCurrentSearch: string;
  productTypeHasMore: boolean;
  commodityCurrentSearch: string;
  // Top Commodity state
  topCommodityData: TopCommodity[];
  topCommodityLastSyncAt: string;
  isLoadingTopCommodity: boolean;
  errorTopCommodity: boolean;
  topCommodityCityCode: string;
  filterMyCommodityOnly: boolean;
}

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "revenue-dashboard"
})
class RevenueDashboardStore extends VuexModule implements RevenueDashboardState {
  topCommodityData: TopCommodity[] = [];
  topCommodityLastSyncAt = "";
  isLoadingTopCommodity = false;
  errorTopCommodity = false;
  topCommodityCityCode = "";
  filterMyCommodityOnly = false;
  public activeMetricFilter: "omzet" | "stt" | "berat" = "omzet";

  public activeBreakdownFilters: BreakdownFilters = {
    serviceTypes: [],
    channels: [],
    productTypes: [],
    destinationCities: [],
    commodities: []
  };

  public productTypeOptions: ProductTypeOption[] = [];
  public isLoadingProductTypes = false;

  public destinationCityOptions: DestinationCityOption[] = [];
  public isLoadingDestinationCities = false;
  public destinationCityCurrentPage = 1;
  public destinationCityHasMore = true;
  public destinationCityCurrentSearch = "";

  public commodityOptions: CommodityOption[] = [];
  public isLoadingCommodities = false;
  public commodityCurrentPage = 1;
  public commodityHasMore = true;
  public productTypeHasMore = true;
  public commodityCurrentSearch = "";
  public productTypeCurrentSearch = "";
  public productTypeCurrentPage = 1;

  @Mutation
  public setActiveMetricFilterMutation(filter: "omzet" | "stt" | "berat") {
    this.activeMetricFilter = filter;
  }

  @Mutation
  public setBreakdownFilterMutation(payload: {
    filterType: keyof BreakdownFilters;
    values: string[];
  }) {
    const { filterType, values } = payload;
    this.activeBreakdownFilters = {
      ...this.activeBreakdownFilters,
      [filterType]: values
    };
  }

 
  @Mutation
  public setProductTypeOptions(options: ProductTypeOption[]): void {
    this.productTypeOptions = options;
  }

  @Mutation
  public setLoadingProductTypes(loading: boolean): void {
    this.isLoadingProductTypes = loading;
  }

  // Mutations untuk Destination Cities
  @Mutation
  public setDestinationCityOptions(options: DestinationCityOption[]): void {
    this.destinationCityOptions = options;
  }

  @Mutation
  public appendDestinationCityOptions(options: DestinationCityOption[]): void {
    const existing = new Map(
      this.destinationCityOptions.map((o) => [o.value.toUpperCase(), o])
    );
    options.forEach((o) => existing.set(o.value.toUpperCase(), o));
    this.destinationCityOptions = Array.from(existing.values());
  }

  @Mutation
  public setLoadingDestinationCities(loading: boolean): void {
    this.isLoadingDestinationCities = loading;
  }

  @Mutation
  public setDestinationCityPagination(payload: {
    page: number;
    hasMore: boolean;
    search: string;
  }): void {
    this.destinationCityCurrentPage = payload.page;
    this.destinationCityHasMore = payload.hasMore;
    this.destinationCityCurrentSearch = payload.search;
  }

  // Mutations untuk Commodities
  @Mutation
  public setCommodityOptions(options: CommodityOption[]): void {
    this.commodityOptions = options;
  }

  @Mutation
  public appendCommodityOptions(options: CommodityOption[]): void {
    const existing = new Map(
      this.commodityOptions.map((o) => [o.value.toUpperCase(), o])
    );
    options.forEach((o) => existing.set(o.value.toUpperCase(), o));
    this.commodityOptions = Array.from(existing.values());
  }

  @Mutation
  public setLoadingCommodities(loading: boolean): void {
    this.isLoadingCommodities = loading;
  }

  @Mutation
  public setCommodityPagination(payload: {
    page: number;
    hasMore: boolean;
    search: string;
  }): void {
    this.commodityCurrentPage = payload.page;
    this.commodityHasMore = payload.hasMore;
    this.commodityCurrentSearch = payload.search;
  }

  @Mutation
  public setProductPagination(payload: {
    page: number;
    hasMore: boolean;
    search: string;
  }): void {
    this.productTypeCurrentPage = payload.page;
    this.productTypeHasMore = payload.hasMore;
    this.productTypeCurrentSearch = payload.search;
  }

  // ====== ACTIONS ======
  @Action
  getRevenueOverview(params: RevenueOverviewApiRequest) {
    const presenter = container.resolve(RevenueDashboardPresenter)

    return presenter.getRevenueOverview(params);
  }

  @Action
  getCustomerLeaderboard(params: RevenueDashboardCustomerListRequest) {
    const presenter = container.resolve(RevenueDashboardPresenter)

    return presenter.getCustomerLeaderboard(params);
  }
  @Action
  public setActiveMetricFilter(filter: "omzet" | "stt" | "berat") {
    this.context.commit("setActiveMetricFilterMutation", filter);
  }

  @Action
  public setBreakdownFilter(payload: {
    filterType: keyof BreakdownFilters;
    values: string[];
  }) {
    this.context.commit("setBreakdownFilterMutation", payload);
  }

  @Action
  public async fetchProductTypes(
    search?: string,
    reset?: boolean
  ): Promise<void> {
    const query = search ?? "";
    const shouldReset = reset === true || search !== this.productTypeCurrentSearch;
    const page = shouldReset ? 1 : this.productTypeCurrentPage;

      if (shouldReset) {
      this.context.commit("setProductTypeOptions", []);
      this.context.commit("setProductPagination", {
        page,
        hasMore: true,
        search: query
      });
    }

    this.context.commit("setLoadingProductTypes", true);
    try {
      const presenter = container.resolve(RevenueDashboardPresenter);
      const fn = (presenter as any)?.getProductTypes;
      let raw: any = [];
      if (typeof fn === "function") {
        raw = await fn.call(presenter, search ?? "");
      } else {
        raw = [];
      }

      const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.items ?? [];

      const mapped: ProductTypeOption[] = list
        .map((item: any) => {
          const rawValue =
            item?.product_type_code ??
            item?.code ??
            item?.value ??
            item?.productCode ??
            "";
          const value = String(rawValue).trim();
          const label = value; 
          return value ? { value, label } : null;
        })
        .filter(Boolean) as ProductTypeOption[];

      const uniq = new Map<string, ProductTypeOption>();
      mapped.forEach((o) => uniq.set(o.value.toUpperCase(), o));
      const dedup = Array.from(uniq.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      this.context.commit("setProductTypeOptions", dedup);
    } catch (err) {
      this.context.commit("setProductTypeOptions", []);
      console.error("Error fetching product types:", err);
    } finally {
      this.context.commit("setLoadingProductTypes", false);
    }
  }


  
  @Action
  public async fetchDestinationCities(
    search?: string,
    reset?: boolean
  ): Promise<void> {
    const query = search ?? "";
    const LIMIT = 20;

    const shouldReset =
      reset === true || query !== this.destinationCityCurrentSearch;

    const page = shouldReset ? 1 : this.destinationCityCurrentPage;

    if (shouldReset) {
      this.context.commit("setDestinationCityOptions", []);
      this.context.commit("setDestinationCityPagination", {
        page,
        hasMore: true,
        search: query
      });
    }

    if (this.isLoadingDestinationCities) return;

    this.context.commit("setLoadingDestinationCities", true);
    try {
      const presenter = container.resolve(RevenueDashboardPresenter);
      const fn = (presenter as any)?.getDestinationCities;
      let raw: any = [];
      if (typeof fn === "function") {
        raw = await fn.call(presenter, query, page, LIMIT);
      } else {
        // Fallback if presenter method not yet implemented
        raw = [];
      }

      const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.items ?? [];

      const mapped: DestinationCityOption[] = list
        .map((item: any) => {
          const value =
            item?.city_code ??
            item?.code ??
            item?.value ??
            item?.three_letter_code ??
            "";
          const label = item?.city_name ?? item?.name ?? item?.label ?? "";
          return value && label
            ? { value: String(value), label: String(label) }
            : null;
        })
        .filter(Boolean) as DestinationCityOption[];

      const p: any = raw?.pagination ?? {};
      const totalData = Number(p?.totalData ?? 0);
      const limitVal = Number(p?.limit ?? LIMIT);
      const pageNum = Number(p?.page ?? page);
      const hasMore = true

      if (shouldReset) {
        this.context.commit("setDestinationCityOptions", mapped);
      } else {
        this.context.commit("appendDestinationCityOptions", mapped);
      }

      this.context.commit("setDestinationCityPagination", {
        page,
        hasMore,
        search: query
      });
    } catch (err) {
      // On error, keep existing options; stop pagination
      this.context.commit("setDestinationCityPagination", {
        page,
        hasMore: false,
        search: query
      });
      console.error("Error fetching destination cities:", err);
    } finally {
      this.context.commit("setLoadingDestinationCities", false);
    }
  }

  @Action
  public async loadMoreDestinationCities(): Promise<void> {
    if (this.isLoadingDestinationCities || !this.destinationCityHasMore) return;
    const nextPage = this.destinationCityCurrentPage + 1;
    this.context.commit("setDestinationCityPagination", {
      page: nextPage,
      hasMore: this.destinationCityHasMore,
      search: this.destinationCityCurrentSearch
    });
    await this.fetchDestinationCities(this.destinationCityCurrentSearch, false);
  }

  @Action
  public async fetchCommodities(
    search?: string,
    reset?: boolean
  ): Promise<void> {
    const query = search ?? "";
    const LIMIT = 20;

    const shouldReset = reset === true || query !== this.commodityCurrentSearch;

    const page = shouldReset ? 1 : this.commodityCurrentPage;

    if (shouldReset) {
      this.context.commit("setCommodityOptions", []);
      this.context.commit("setCommodityPagination", {
        page,
        hasMore: true,
        search: query
      });
    }

    if (this.isLoadingCommodities) return;

    this.context.commit("setLoadingCommodities", true);
    try {
      const presenter = container.resolve(RevenueDashboardPresenter);
      const fn = (presenter as any)?.getCommodities;
      let raw: any = [];
      if (typeof fn === "function") {
        raw = await fn.call(presenter, query, page, LIMIT);
      } else {
        // Fallback if presenter method not yet implemented
        raw = [];
      }

      const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.items ?? [];

      const mapped: CommodityOption[] = list
        .map((item: any) => {
          const value = item?.commodity_name ?? item?.name ?? item?.label ?? "";
          const label = item?.commodity_name ?? item?.name ?? item?.label ?? "";
          return value && label
            ? { value: String(value), label: String(label) }
            : null;
        })
        .filter(Boolean) as CommodityOption[];

      const p: any = raw?.pagination ?? {};
      const totalData = Number(p?.totalData ?? 0);
      const limitVal = Number(p?.limit ?? LIMIT);
      const pageNum = Number(p?.page ?? page);
      const hasMore =
        typeof p?.hasNext === "boolean"
          ? !!p.hasNext
          : pageNum * limitVal < totalData || mapped.length === LIMIT;

      if (shouldReset) {
        this.context.commit("setCommodityOptions", mapped);
      } else {
        this.context.commit("appendCommodityOptions", mapped);
      }

      this.context.commit("setCommodityPagination", {
        page,
        hasMore,
        search: query
      });
    } catch (err) {
      this.context.commit("setCommodityPagination", {
        page,
        hasMore: false,
        search: query
      });
      console.error("Error fetching commodities:", err);
    } finally {
      this.context.commit("setLoadingCommodities", false);
    }
  }

  @Action
  public async loadMoreCommodities(): Promise<void> {
    if (this.isLoadingCommodities || !this.commodityHasMore) return;
    const nextPage = this.commodityCurrentPage + 1;
    this.context.commit("setCommodityPagination", {
      page: nextPage,
      hasMore: this.commodityHasMore,
      search: this.commodityCurrentSearch
    });
    await this.fetchCommodities(this.commodityCurrentSearch, false);
  }

  

  @Action
  public async loadMoreProductTypes(): Promise<void> {
    if (this.isLoadingProductTypes || !this.productTypeHasMore) return;
    const nextPage = this.productTypeCurrentPage + 1;
    this.context.commit("setProductPagination", {
      page: nextPage,
      hasMore: this.productTypeHasMore,
      search: this.productTypeCurrentSearch
    });
    await this.fetchProductTypes(this.productTypeCurrentSearch, false);
  }

  @Action
  public async debugSmokeProductTypes(search?: string): Promise<void> {
    const presenter = container.resolve(RevenueDashboardPresenter);
    const raw = await presenter.getProductTypes(search ?? "");
  }

  @Action
  public async debugSmokeDestinationCities(
    search?: string,
    page?: number,
    limit?: number
  ): Promise<void> {
    const presenter = container.resolve(RevenueDashboardPresenter);
    const raw = await presenter.getDestinationCities(
      search ?? "",
      page ?? 1,
      limit ?? 20
    );
  }

  @Action
  public async debugSmokeCommodities(
    search?: string,
    page?: number,
    limit?: number
  ): Promise<void> {
    const presenter = container.resolve(RevenueDashboardPresenter);
    const raw = await presenter.getCommodities(
      search ?? "",
      page ?? 1,
      limit ?? 20
    );
  }

  @Action
  public async fetchRevenueBreakdown(params: RevenueBreakdownApiRequest) {
    const presenter = container.resolve(RevenueDashboardPresenter)
    return presenter.getRevenueBreakdown(params);
  }

  // ====== TOP COMMODITY MUTATIONS ======
  @Mutation
  public setLoadingTopCommodity(loading: boolean): void {
    this.isLoadingTopCommodity = loading;
  }

  @Mutation
  public setErrorTopCommodity(error: boolean): void {
    this.errorTopCommodity = error;
  }

  @Mutation
  public setTopCommodityData(items: TopCommodity[]): void {
    this.topCommodityData = items;
  }

  @Mutation
  public setTopCommodityLastSyncAt(ts: string): void {
    this.topCommodityLastSyncAt = ts ?? "";
  }

  @Mutation
  public setTopCommodityCityCode(code: string): void {
    this.topCommodityCityCode = code;
  }

  @Mutation
  public setFilterMyCommodityOnlyMutation(active: boolean): void {
    this.filterMyCommodityOnly = active;
  }

  // ====== TOP COMMODITY ACTION ======
  @Action
  public async fetchTopCommodityData(params?: Record<string, any>): Promise<void> {
    if (this.isLoadingTopCommodity) return;

    this.context.commit("setLoadingTopCommodity", true);
    this.context.commit("setErrorTopCommodity", false);

    try {
      const presenter = container.resolve(PosRevenuePresenter);
      const resp = await presenter.getTopCommodity(params ?? {});

      const items = Array.isArray(resp?.data) ? resp.data : [];
      const lastSyncAt = resp?.lastSyncAt ?? "";
      const cityCode = resp?.cityCode ?? "";

     
      this.context.commit("setTopCommodityData", items);
      this.context.commit("setTopCommodityLastSyncAt", lastSyncAt);
      this.context.commit("setTopCommodityCityCode", cityCode);

    } catch (err) {
      console.error("[RevenueDashboardController] Gagal fetch top commodity:", err);
      this.context.commit("setErrorTopCommodity", true);
      this.context.commit("setTopCommodityData", []);
      this.context.commit("setTopCommodityLastSyncAt", "");
      this.context.commit("setTopCommodityCityCode", "");
    } finally {
      this.context.commit("setLoadingTopCommodity", false);
    }
  }


  @Action
  public setFilterMyCommodityOnly(active: boolean): void {
    this.context.commit("setFilterMyCommodityOnlyMutation", !!active);
  }

 
  

  private _customerLeaderboardSearchQuery = "";
  private _activeCustomerLeaderboardTags: string[] = [];

  get customerLeaderboardSearchQuery(): string {
    return this._customerLeaderboardSearchQuery;
  }
  get activeCustomerLeaderboardTags(): string[] {
    return this._activeCustomerLeaderboardTags;
  }

  @Mutation
  public setCustomerLeaderboardSearchQueryMutation(query: string): void {
    this._customerLeaderboardSearchQuery = query ?? "";
  }

  @Mutation
  public setCustomerLeaderboardTagsMutation(tags: string[]): void {
    this._activeCustomerLeaderboardTags = Array.isArray(tags) ? tags : [];
  }

  @Action
  public setCustomerLeaderboardSearch(query: string): void {
    this.context.commit("setCustomerLeaderboardSearchQueryMutation", query ?? "");
  }

  @Action
  public setCustomerLeaderboardTags(tags: string[]): void {
    this.context.commit("setCustomerLeaderboardTagsMutation", Array.isArray(tags) ? tags : []);
  }

 
}

export const RevenueDashboardController = getModule(RevenueDashboardStore);


